import logger from '../utils/logger';
import { apiGet, apiPost, apiDelete } from './api';

/**
 * Git integration for Studio artifacts.
 *
 * Backs the shared Git status bar wherever it is mounted, the pipeline
 * designer, the microservices canvas, the file pipeline designer and the dock's
 * script editors, so Git behaves identically across all of them.
 *
 * Access tokens are write-only: they are sent on link and never come back.
 */
export const gitService = {
  listRepositories: async () => {
    try {
      return await apiGet('git/repositories');
    } catch (error) {
      logger.error('Failed to list Git repositories:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  /**
   * Bind a repository. Re-linking the same remote and path prefix updates the
   * existing binding rather than creating a duplicate.
   */
  linkRepository: async ({
    name,
    provider = 'GITHUB',
    remoteUrl,
    defaultBranch = 'main',
    pathPrefix = 'studio',
    authUsername,
    authToken,
    autocommit = false,
  }) => {
    try {
      return await apiPost('git/repositories', {
        name,
        provider,
        remoteUrl,
        defaultBranch,
        pathPrefix,
        authUsername,
        authToken,
        autocommit,
      });
    } catch (error) {
      logger.error('Failed to link Git repository:', error);
      return { success: false, error: error.message };
    }
  },

  unlinkRepository: async (repositoryId) => {
    try {
      return await apiDelete(`git/repositories/${repositoryId}`);
    } catch (error) {
      logger.error('Failed to unlink Git repository:', error);
      return { success: false, error: error.message };
    }
  },

  getStatus: async (repositoryId) => {
    try {
      return await apiGet(`git/repositories/${repositoryId}/status`);
    } catch (error) {
      logger.error('Failed to read Git status:', error);
      return { success: false, error: error.message };
    }
  },

  listBranches: async (repositoryId) => {
    try {
      return await apiGet(`git/repositories/${repositoryId}/branches`);
    } catch (error) {
      logger.error('Failed to list branches:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  /**
   * Commit history. Pass a repo-relative `path` to narrow it to one artifact.
   * This is what per-pipeline and per-script history views use.
   */
  getHistory: async (repositoryId, { path, limit = 50 } = {}) => {
    try {
      const params = new URLSearchParams();
      if (path) params.set('path', path);
      params.set('limit', String(limit));
      return await apiGet(`git/repositories/${repositoryId}/history?${params.toString()}`);
    } catch (error) {
      logger.error('Failed to read Git history:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  /** Recent Git operations Studio performed for this repository. The audit trail. */
  getActivity: async (repositoryId, { limit = 20 } = {}) => {
    try {
      return await apiGet(`git/repositories/${repositoryId}/activity?limit=${limit}`);
    } catch (error) {
      logger.error('Failed to read Git activity:', error);
      return { success: false, data: [] };
    }
  },

  /**
   * Serialize artifacts and commit them. `push` defaults to false, pushing
   * publishes outside Studio, so it stays an explicit action rather than
   * something a save does silently.
   */
  commit: async (repositoryId, { message, entityTypes, artifactType, artifactId, push = false }) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/commit`, {
        message,
        entityTypes,
        artifactType,
        artifactId,
        push,
      });
    } catch (error) {
      logger.error('Failed to commit to Git:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * What importing the repository would change in Studio, changes nothing.
   * Always call this before `importFromGit` so the user sees the effect first:
   * import overwrites their working artifacts from a source they may not have
   * authored.
   */
  previewImport: async (repositoryId, { pull = true } = {}) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/import/preview?pull=${pull}`, {});
    } catch (error) {
      logger.error('Failed to preview import:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Apply the repository's artifacts into Studio.
   *
   * `conflictResolution` defaults to 'skip'. Existing artifacts are left alone
   * unless the caller explicitly asks to overwrite.
   */
  importFromGit: async (repositoryId, { conflictResolution = 'skip', pull = true } = {}) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/import`, { conflictResolution, pull });
    } catch (error) {
      logger.error('Failed to import from Git:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Open a pull request for the current branch. The backend pushes the branch
   * first, since a PR for a branch the remote has never seen is rejected.
   */
  openPullRequest: async (repositoryId, { title, body, base } = {}) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/pull-request`, { title, body, base });
    } catch (error) {
      logger.error('Failed to open pull request:', error);
      return { success: false, error: error.message };
    }
  },

  /** Existing open PR for the current branch, so the UI can link instead of duplicating. */
  findPullRequest: async (repositoryId) => {
    try {
      return await apiGet(`git/repositories/${repositoryId}/pull-request`);
    } catch (error) {
      logger.debug('No open pull request found:', error);
      return { success: false, data: null };
    }
  },

  /**
   * Arm or disarm the CI webhook. The secret is generated server-side and
   * returned exactly once, in this response. It is never readable again.
   * Pass `branch: null` to disarm.
   */
  configureWebhook: async (repositoryId, { branch, conflictResolution = 'overwrite' } = {}) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/webhook`, { branch, conflictResolution });
    } catch (error) {
      logger.error('Failed to configure webhook:', error);
      return { success: false, error: error.message };
    }
  },

  push: async (repositoryId) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/push`, {});
    } catch (error) {
      logger.error('Failed to push:', error);
      return { success: false, error: error.message };
    }
  },

  pull: async (repositoryId) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/pull`, {});
    } catch (error) {
      logger.error('Failed to pull:', error);
      return { success: false, error: error.message };
    }
  },

  checkout: async (repositoryId, branch, create = false) => {
    try {
      return await apiPost(`git/repositories/${repositoryId}/checkout`, { branch, create });
    } catch (error) {
      logger.error('Failed to switch branch:', error);
      return { success: false, error: error.message };
    }
  },
};

export default gitService;
